import React from 'react';
import { ISearchOptionsProps } from './QuickSearchTag';

// Generic interface for any item with an ID and display text
export interface TagPickerItem {
    id: string | number;
    displayText: string;
    secondaryText?: string;
    key?: React.Key | null; // Added for QuickSearchTag compatibility
    text?: string | null; // Added for QuickSearchTag compatibility
    imageUrl?: string | null; // Added for QuickSearchTag compatibility
}

// Search function type - using generic for context type safety
export type SearchFunction<T extends TagPickerItem, TContext = unknown> = (searchTerm: string, context?: TContext) => Promise<T[]>;
export type PrepopulateFunction<T extends TagPickerItem, TContext = unknown> = (context?: TContext) => Promise<T[]>;

export interface GenericTagPickerProps<T extends TagPickerItem, TContext = unknown> {
    // Data and selection
    selectedItem?: T | null;
    selectedItems?: T[];

    // Configuration
    multiSelect?: boolean;
    disabled?: boolean;
    isLoading?: boolean;
    error?: string;
    label: string;
    placeholder?: string;
    required?: boolean;

    // Search configuration
    searchFunction?: SearchFunction<T, TContext>;
    prepopulateFunction?: PrepopulateFunction<T, TContext>;
    searchContext?: TContext; // Additional context for search functions - now properly typed
    minSearchLength?: number; // Minimum characters to trigger server search
    searchDebounceMs?: number; // Debounce delay for search
    enableWildcardSearch?: boolean; // Enable '%' wildcard search behavior (default: true)

    // Callbacks
    onItemChange?: (item: T | null) => void; // For single select
    onItemsChange?: (items: T[]) => void; // For multi-select

    // Custom rendering
    renderOption?: (item: T) => React.ReactNode; // Custom rendering for dropdown options

    // QuickSearchTag-related props
    hideCoin?: boolean; // Hide avatar/coin in tags
    customAvatar?: (option: ISearchOptionsProps) => React.JSX.Element; // Custom avatar renderer for tags
    isContact?: boolean; // Flag to indicate if items represent contacts (for avatar styling)
}

// Adapter function to convert TagPickerItem to ISearchOptionsProps format
export const adaptItemForQuickSearchTag = <T extends TagPickerItem>(item: T): ISearchOptionsProps => ({
    key: item.key ?? item.id,
    text: item.text ?? item.displayText,
    displayText: item.displayText, // Pass through the full description for tooltip
    secondaryText: item.secondaryText,
    imageUrl: item.imageUrl,
    className: undefined,
    showSecondaryText: true
});

export const noMatchesToken = "no-matches";
